import { View, Text } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { RotateCcw, Zap, Server, Globe } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  APP_MODE,
  AppMode,
  modeDescriptions,
  saveModeOverride,
  clearModeOverride,
  restartApp,
  isHostnameControlled,
  isEnvControlled,
  hasActiveOverride,
  getDefaultMode,
} from '@/lib/config/app-mode';

const MODE_OPTIONS: AppMode[] = ['mock', 'demo', 'production'];

const MODE_ICONS: Record<AppMode, typeof Zap> = {
  mock: Zap,
  demo: Server,
  production: Globe,
};

const MODE_COLORS: Record<AppMode, string> = {
  mock: '#F59E0B',     // amber
  demo: '#3B82F6',     // blue
  production: '#10B981', // emerald
};

export default function DevModeToggle() {
  const colors = useThemeColors();
  const { confirm } = useAlert();

  const hostnameControlled = isHostnameControlled();
  // Env-pinned builds can't be switched at runtime (S15) — same UX as hostname.
  const modeLocked = hostnameControlled || isEnvControlled();
  const overrideActive = hasActiveOverride();
  const defaultMode = getDefaultMode();

  const handleModeSelect = async (mode: AppMode) => {
    if (mode === APP_MODE) return;
    if (modeLocked) return;

    const info = modeDescriptions[mode];
    const confirmed = await confirm(
      `Switch to ${info.label}?`,
      `${info.description}. The app will restart to apply this change.`
    );

    if (confirmed) {
      await saveModeOverride(mode);
      await restartApp();
    }
  };

  const handleClearOverride = async () => {
    const defaultInfo = modeDescriptions[defaultMode];
    const confirmed = await confirm(
      'Clear Mode Override?',
      `The app will restart and revert to ${defaultInfo.label} (${defaultInfo.description}).`
    );

    if (confirmed) {
      await clearModeOverride();
      await restartApp();
    }
  };

  return (
    <Animated.View entering={FadeIn.duration(300)}>
      {/* Mode selector */}
      <View
        className="flex-row rounded-xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.secondary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {MODE_OPTIONS.map((mode) => {
          const isActive = mode === APP_MODE;
          const Icon = MODE_ICONS[mode];
          const modeColor = MODE_COLORS[mode];
          const info = modeDescriptions[mode];

          return (
            <AnimatedPressable
              key={mode}
              onPress={() => handleModeSelect(mode)}
              disabled={modeLocked}
              className="flex-1 items-center py-3 px-2"
              style={[
                isActive && {
                  backgroundColor: `${modeColor}15`,
                  borderBottomWidth: 3,
                  borderBottomColor: modeColor,
                },
                !isActive && {
                  borderBottomWidth: 3,
                  borderBottomColor: 'transparent',
                },
                modeLocked && !isActive && { opacity: 0.3 },
              ]}
            >
              <Icon
                size={18}
                color={isActive ? modeColor : colors.text.tertiary}
              />
              <Text
                className="text-xs font-semibold mt-1"
                style={{ color: isActive ? modeColor : colors.text.tertiary }}
              >
                {info.label.replace(' Mode', '')}
              </Text>
            </AnimatedPressable>
          );
        })}
      </View>

      {/* Status info */}
      <View className="mt-3 px-1">
        <Text className="text-xs" style={{ color: colors.text.tertiary }}>
          {hostnameControlled
            ? `Mode set by hostname (${typeof window !== 'undefined' ? window.location?.hostname : ''})`
            : modeLocked
              ? 'Mode pinned by build environment (EXPO_PUBLIC_APP_MODE)'
              : overrideActive
                ? `Override active (default: ${modeDescriptions[defaultMode].label})`
                : `Default mode from ${APP_MODE === defaultMode ? 'environment' : 'auto-detect'}`}
        </Text>
      </View>

      {/* Clear override button */}
      {overrideActive && !modeLocked && (
        <AnimatedPressable
          onPress={handleClearOverride}
          className="flex-row items-center justify-center gap-2 mt-3 py-2.5 rounded-lg"
          style={{
            backgroundColor: colors.surface.secondary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <RotateCcw size={14} color={colors.text.secondary} />
          <Text className="text-xs font-medium" style={{ color: colors.text.secondary }}>
            Clear Override (revert to {modeDescriptions[defaultMode].label})
          </Text>
        </AnimatedPressable>
      )}
    </Animated.View>
  );
}
